import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export const ReassignAdminDialog = ({ open, onOpenChange, bills, adminUsers, onDone }) => {
  const [selected, setSelected] = useState([]);
  const [adminId, setAdminId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setSelected(bills.map((b) => b._id)); setAdminId(""); }
  }, [open, bills]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const total = bills.filter((b) => selected.includes(b._id)).reduce((s, b) => s + (b.remaining || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bills/reassign-admin", { bill_ids: selected, admin_id: adminId });
      toast.success(`${r.data.moved} tagihan dipindahkan ke ${r.data.admin_name}`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Pindahkan Tagihan "Tanpa Admin"</DialogTitle>
          <DialogDescription>Pilih tagihan lalu admin yang benar. Perubahan tercatat di audit log.</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto border rounded-lg divide-y" data-testid="reassign-bill-list">
          {bills.length === 0 && <p className="p-4 text-sm text-muted-foreground">Tidak ada tagihan Tanpa Admin.</p>}
          {bills.map((b) => (
            <label key={b._id} className="flex items-start gap-3 p-3 text-sm cursor-pointer hover:bg-muted/40 transition-colors">
              <input type="checkbox" className="mt-1 accent-[#0A1633]" checked={selected.includes(b._id)} onChange={() => toggle(b._id)} data-testid={`reassign-check-${b._id}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{b.invoice_no} · {b.supplier_name}</p>
                <p className="text-xs text-muted-foreground break-words">{b.description || "-"} · {formatDate(b.issue_date)}</p>
              </div>
              <span className="text-xs font-semibold text-rose-600 whitespace-nowrap">{formatIDR(b.remaining)}</span>
            </label>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label>Admin tujuan</Label>
          <Select value={adminId} onValueChange={setAdminId}>
            <SelectTrigger data-testid="reassign-admin-select"><SelectValue placeholder="Pilih admin" /></SelectTrigger>
            <SelectContent>{adminUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="reassign-summary">{selected.length} tagihan dipilih · sisa hutang {formatIDR(total)}</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reassign-cancel-btn">Batal</Button>
          <Button variant="gold" disabled={busy || !adminId || selected.length === 0} onClick={submit} data-testid="reassign-submit-btn">
            {busy ? "Memindahkan..." : "Pindahkan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
